import React from "react";
import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";
import moment from "moment";
import { decrypt } from "../../utils";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fbfaf8",
    paddingTop: 24,
    paddingBottom: 20,
    paddingHorizontal: 24,
    fontSize: 9,
    color: "#1f2937",
    fontFamily: "Helvetica",
  },
  hero: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1 solid #e9e3da",
    padding: 12,
    marginBottom: 10,
  },
  brandRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  logoImg: {
    width: 32,
    height: 32,
    borderRadius: 16,
    objectFit: "cover",
    marginRight: 8,
  },
  logoCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#f0b437",
    justifyContent: "center",
    alignItems: "center",
    marginRight: 8,
  },
  logoText: {
    fontSize: 13,
    fontWeight: 700,
    color: "#ffffff",
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 6,
  },
  meta: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  metaItem: {
    fontSize: 8,
    color: "#7b8190",
    marginRight: 12,
    marginBottom: 2,
  },
  section: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    border: "1 solid #e9e3da",
    overflow: "hidden",
    marginBottom: 10,
  },
  sectionHeader: {
    backgroundColor: "#f3eee6",
    paddingHorizontal: 10,
    paddingVertical: 6,
  },
  sectionHeaderText: {
    fontSize: 9,
    fontWeight: 700,
    color: "#383d45",
    textTransform: "uppercase",
  },
  sectionBody: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  rowGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginHorizontal: -5,
  },
  rowHalf: {
    width: "50%",
    paddingHorizontal: 5,
    marginBottom: 7,
  },
  rowFull: {
    width: "100%",
    paddingHorizontal: 5,
    marginBottom: 7,
  },
  label: {
    fontSize: 7,
    textTransform: "uppercase",
    color: "#9b9fa8",
    marginBottom: 3,
    fontWeight: 700,
  },
  value: {
    fontSize: 8.5,
    color: "#202124",
    lineHeight: 1.35,
  },
  signatureImage: {
    width: 110,
    height: 36,
    objectFit: "contain",
  },
  signatureText: {
    fontSize: 16,
    color: "#2d3138",
  },
  narrativeBox: {
    backgroundColor: "#f6f1ea",
    borderRadius: 7,
    padding: 8,
    marginTop: 2,
  },
  narrativeText: {
    fontSize: 8,
    lineHeight: 1.35,
    color: "#626976",
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginBottom: 4,
  },
  chip: {
    borderRadius: 10,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginRight: 6,
    marginBottom: 4,
    backgroundColor: "#fff6dc",
    border: "1 solid #f0b437",
  },
  chipText: {
    fontSize: 7,
    color: "#8c6208",
    fontWeight: 700,
  },
  footer: {
    position: "absolute",
    left: 24,
    right: 24,
    bottom: 10,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 6,
    color: "#c0b6a8",
  },
});

const getInitials = (name) => {
  if (!name) return "F";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

const Logo = ({ photo, name }) => {
  if (photo && photo !== "undefined" && photo !== "null") {
    return <Image src={photo} style={styles.logoImg} />;
  }
  return (
    <View style={styles.logoCircle}>
      <Text style={styles.logoText}>{getInitials(name)}</Text>
    </View>
  );
};

const DisplayField = ({ label, value, full = false }) => (
  <View style={full ? styles.rowFull : styles.rowHalf}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || "—"}</Text>
  </View>
);

const boolText = (value) => (value ? "Yes" : "No");
const joinValue = (value) => (Array.isArray(value) ? value.join(", ") : value);
const formatDate = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : value;
};
const formatTime = (value) => {
  if (!value) return "—";
  if (typeof value === "string") {
    const parsedString = moment(value, ["HH:mm", "hh:mm A", moment.ISO_8601], true);
    if (parsedString.isValid()) return parsedString.format("hh:mm A");
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};
const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("MM/DD/YYYY hh:mm A") : value;
};
const safeDecrypt = (value) => {
  if (!value) return null;
  try {
    return decrypt(value) || null;
  } catch {
    return value;
  }
};

const IncidentReportPDF = ({ clientDetails, profile, form }) => {
  const companyName =
    profile?.business?.companyName ||
    clientDetails?.business?.companyName ||
    profile?.facility?.name ||
    "Facility";
  const facilityPhoto = safeDecrypt(
    clientDetails?.facility?.imageurl || profile?.facility?.imageurl
  );
  const signatureImage =
    safeDecrypt(form?.signatureData?.signature) ||
    safeDecrypt(form?.signatureData?.signatureImage) ||
    safeDecrypt(form?.signatureData?.signatureInitials) ||
    null;
  const signatureText =
    form?.signatureData?.signatureText ||
    form?.signatureData?.signatureInitialsText ||
    "";

  return (
    <Document title={`${clientDetails?.name || "Resident"} Incident Report`}>
      <Page size="A4" style={styles.page}>
        <View style={styles.hero}>
          <View style={styles.brandRow}>
            <Logo photo={facilityPhoto} name={companyName} />
          </View>
          <Text style={styles.title}>Incident Report</Text>
          <View style={styles.meta}>
            <Text style={styles.metaItem}>{clientDetails?.name || "Resident"}</Text>
            <Text style={styles.metaItem}>{companyName}</Text>
            <Text style={styles.metaItem}>{`Incident date: ${formatDate(form?.incidentDate)}`}</Text>
            <Text style={styles.metaItem}>{`Completed: ${formatDate(form?.dateCompleted || moment())}`}</Text>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Incident Identification</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.rowGrid}>
              <DisplayField label="Date of incident" value={formatDate(form?.incidentDate)} />
              <DisplayField label="Time of incident" value={formatTime(form?.incidentTime)} />
              <DisplayField label="Location of incident" value={form?.incidentLocation} full />
              <DisplayField label="Resident(s) involved" value={joinValue(form?.residentsInvolved)} />
              <DisplayField label="Staff involved / witnesses" value={joinValue(form?.staffInvolved)} />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Narrative</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.narrativeBox}>
              <Text style={styles.narrativeText}>{form?.narrative || "—"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Injury / Medical Response</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.chipRow}>
              <View style={styles.chip}>
                <Text style={styles.chipText}>
                  {`Physician or hospital visit: ${boolText(form?.physicianOrHospitalVisit)}`}
                </Text>
              </View>
              <View style={styles.chip}>
                <Text style={styles.chipText}>
                  {`Police involved: ${boolText(form?.policeInvolved)}`}
                </Text>
              </View>
            </View>
            <View style={styles.rowGrid}>
              <DisplayField label="Physician / hospital name" value={form?.physicianHospitalName} />
              <DisplayField label="Phone number" value={form?.physicianHospitalPhone} />
                  <DisplayField label="Date / time transported" value={formatDateTime(form?.transportedDateTime)} />
                  <DisplayField
                    label="How transported"
                    value={form?.notTransported ? "Not transported" : form?.transportedHow}
                  />
              <DisplayField label="Physician / hospital address" value={form?.physicianHospitalAddress} full />
              {form?.policeInvolved ? (
                <>
                  <DisplayField label="Officer name" value={form?.officerName} />
                  <DisplayField label="Badge number" value={form?.badgeNumber} />
                  <DisplayField label="Report number" value={form?.reportNumber} full />
                </>
              ) : null}
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Notifications</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.chipRow}>
              <View style={styles.chip}>
                <Text style={styles.chipText}>{`Regulator notified: ${boolText(form?.adhsNotified)}`}</Text>
              </View>
            </View>
            <View style={styles.rowGrid}>
              <DisplayField label="Authorized representative" value={form?.authorizedRepName} />
              <DisplayField label="Date / time notified" value={formatDateTime(form?.authorizedRepDateTime)} />
              <DisplayField label="Primary care physician" value={form?.pcpName} />
              <DisplayField label="Date / time notified" value={formatDateTime(form?.pcpDateTime)} />
              <DisplayField label="Other persons notified" value={form?.otherNotifiedName} />
              <DisplayField label="Date / time notified" value={formatDateTime(form?.otherNotifiedDateTime)} />
            </View>
          </View>
        </View>

        {Array.isArray(form?.selectedProviders) && form.selectedProviders.length > 0 ? (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>Providers Notified</Text>
            </View>
            <View style={styles.sectionBody}>
              <View style={styles.rowGrid}>
                {form.selectedProviders.map((provider, index) => (
                  <DisplayField
                    key={provider?.key || index}
                    label={
                      provider?.type
                        ? `${provider?.name || "Provider"} · ${provider.type}`
                        : provider?.name || "Provider"
                    }
                    value={
                      [provider?.phone, provider?.email].filter(Boolean).join("  ·  ") || "—"
                    }
                  />
                ))}
              </View>
            </View>
          </View>
        ) : null}

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Evaluation</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.chipRow}>
              <View style={styles.chip}>
                <Text style={styles.chipText}>{`Actual harm occurred: ${boolText(form?.actualHarmOccurred)}`}</Text>
              </View>
              <View style={styles.chip}>
                <Text style={styles.chipText}>{`Potential for harm exists: ${boolText(form?.potentialForHarm)}`}</Text>
              </View>
            </View>
            <View style={styles.narrativeBox}>
              <Text style={styles.narrativeText}>{form?.residentConditionChange || "—"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Actions</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.rowGrid}>
              <DisplayField label="Immediate action taken" value={form?.actionTakenImmediately} full />
              <DisplayField label="Action taken to prevent recurrence" value={form?.actionTakenPreventRecurrence} full />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Investigation</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.chipRow}>
              <View style={styles.chip}>
                <Text style={styles.chipText}>{`Abuse / neglect suspected: ${boolText(form?.abuseNeglectSuspected)}`}</Text>
              </View>
            </View>
            <View style={styles.rowGrid}>
              <DisplayField label="Dates / times of suspected incident" value={formatDateTime(form?.suspectedIncidentDateTime)} />
              <DisplayField label="Names of witnesses" value={form?.witnessNames} />
              <DisplayField label="Description of suspected incident" value={form?.suspectedIncidentDescription} full />
              <DisplayField label="Actions taken to prevent recurrence" value={form?.investigationPreventionActions} full />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionHeaderText}>Sign-off</Text>
          </View>
          <View style={styles.sectionBody}>
            <View style={styles.rowGrid}>
              <DisplayField label="Printed name" value={form?.completedBy} />
              <View style={styles.rowHalf}>
                <Text style={styles.label}>Signature</Text>
                {signatureImage ? (
                  <Image src={signatureImage} style={styles.signatureImage} />
                ) : signatureText ? (
                  <Text style={styles.signatureText}>{signatureText}</Text>
                ) : (
                  <Text style={styles.value}>—</Text>
                )}
              </View>
              <DisplayField label="Date completed" value={formatDate(form?.dateCompleted)} />
            </View>
          </View>
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{`${companyName} · Incident Report`}</Text>
          <Text
            style={styles.footerText}
            render={({ pageNumber, totalPages }) =>
              `Generated ${moment().format("MM/DD/YYYY")} · Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default IncidentReportPDF;
